import clsx from 'clsx';
import { APPS } from 'constants/appModules';
import { PATHS } from 'constants/paths';
import { isAllowedToWellGuardian } from 'helpers/UserHelper';
import { getAppFromPath } from 'helpers/pathParser';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useEffect, useRef, useState } from 'react';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import { useSelectUser } from 'redux/User/selectors';

import styles from './Sidebar.module.scss';
import SidebarContentDrillX from './components/SidebarContentDrillX';
import SidebarContentEnergyEnergy from './components/SidebarContentEnergyEffiency/SidebarContentEnergyEffiency';
import SidebarContentWellGuardian from './components/SidebarContentWellGuardian';

type Props = {
  isOpen: boolean;
  onSidebarChanged: () => void;
};

export const useSidebar = ({ isOpen, onSidebarChanged }: Props) => {
  const match = useRouteMatch();
  const history = useHistory();
  const user = useSelectUser();
  const currentApp = getAppFromPath(match.path);

  const isWellGuardianDisabled = !isAllowedToWellGuardian(user?.profile);

  const [selectedApp, setSelectedApp] = useState<HTMLElement | string>(currentApp);
  const [isAppChanged, setAppChanged] = useState<boolean>(false);
  const moduleSelectContainer = useRef(null);

  const isOpenDelayed = useDebouncedValue(isOpen, parseInt(styles.transitionDurationInMs));

  // open and close are specific selector names in css to track the sidebar state
  const getStylePerState = (basicStyle: string) =>
    clsx(basicStyle, isOpen ? styles.open : styles.close);

  const handleLinkChange = () => PATHS.WELL_GUARDIAN_WELL_HEALTH_HOME;

  const onChangeApp = (event: any) => {
    setSelectedApp(event.target.value);
    setAppChanged(true);
  };

  // this useEffect is used to set the selected app , when moving back to the previous page
  useEffect(() => {
    if (currentApp === APPS.WELL_GUARDIAN) {
      setSelectedApp(APPS.WELL_GUARDIAN);
    } else if (currentApp === APPS.DRILLX_SMART_OPERATIONS) {
      setSelectedApp(APPS.DRILLX_SMART_OPERATIONS);
    } else if (currentApp === APPS.ENERGY_EFFICIENCY) {
      setSelectedApp(APPS.ENERGY_EFFICIENCY);
    }
  }, [currentApp]);

  // route to the new app, when selected app has changed
  useEffect(() => {
    if (isAppChanged) {
      if (selectedApp === APPS.DRILLX_SMART_OPERATIONS) {
        history.push(generatePath(PATHS.HOME));
      } else if (selectedApp === APPS.WELL_GUARDIAN) {
        history.push(generatePath(handleLinkChange()));
      } else {
        history.push(generatePath(PATHS.ENERGY_EFFICIENCY_HOME));
      }
    }
    setAppChanged(false);
  }, [history, isAppChanged, selectedApp]);

  const getSideBarComponentToRender = () => {
    if (currentApp === APPS.WELL_GUARDIAN) {
      return <SidebarContentWellGuardian />;
    } else if (currentApp === APPS.DRILLX_SMART_OPERATIONS) {
      return <SidebarContentDrillX />;
    } else if (currentApp === APPS.ENERGY_EFFICIENCY) {
      return <SidebarContentEnergyEnergy />;
    }
  };

  return {
    getStylePerState,
    isOpenDelayed,
    moduleSelectContainer,
    selectedApp,
    onChangeApp,
    isWellGuardianDisabled,
    getSideBarComponentToRender,
  };
};
